import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * Swym's wishlist add-to-cart buttons (in the ghost drawer / wishlist page)
 * add to cart directly via their own AJAX call, bypassing the theme's
 * product-form flow entirely. That means none of our cart UI - the header
 * bubble count, cart drawer auto-open, cart line items - hears about it.
 *
 * `sw:addedtocart` is Swym's own event, fired once their add-to-cart request
 * succeeds, so we use it to re-sync: fetch the fresh cart and dispatch the
 * same CartAddEvent the theme's product form dispatches on a normal add.
 * @param {{ evtLayer: EventTarget }} swat
 */
function syncSwymCartAdditions(swat) {
  swat.evtLayer.addEventListener('sw:addedtocart', async () => {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    if (!response.ok) return;

    const cart = await response.json();

    // The bubble/liquid render excludes the transit cover line item from the
    // displayed count (see cart-bubble.liquid) - mirror that here, since
    // cart.item_count from the raw cart JSON includes it.
    const transitCoverQuantity = cart.items
      .filter((item) => item.handle === 'transit-cover')
      .reduce((sum, item) => sum + item.quantity, 0);

    document.dispatchEvent(
      new CartAddEvent(cart, 'swym-wishlist', {
        source: 'swym-wishlist',
        itemCount: cart.item_count - transitCoverQuantity,
      })
    );
  });
}

if (window._swat) {
  syncSwymCartAdditions(window._swat);
} else {
  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(syncSwymCartAdditions);
}

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 * @property {HTMLElement} [wishlistBubble] - The wishlist bubble element.
 * @property {HTMLElement} [wishlistBubbleCount] - The wishlist bubble count element.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener('SwymWishlistFetched', this.#onWishlistFetched);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener('SwymWishlistFetched', this.#onWishlistFetched);
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };

  /**
   * Handles the Swym wishlist fetch event and updates the wishlist bubble
   * count, excluding items Swym tracks internally under the "auto-wishlist"
   * source rather than ones a shopper explicitly added.
   * @param {CustomEvent<{ defaultList?: { listcontents?: { source?: string }[] } }>} event
   */
  #onWishlistFetched = (event) => {
    if (!this.refs.wishlistBubble || !this.refs.wishlistBubbleCount) return;

    const items = event.detail?.defaultList?.listcontents ?? [];
    const count = items.filter((item) => item.source !== 'auto-wishlist').length;

    this.refs.wishlistBubbleCount.textContent = count > 0 ? String(count) : '';
    this.refs.wishlistBubble.classList.toggle('visually-hidden', count === 0);
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
